<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import type { EnquiryCount } from "./services/enquiryData";
  import type { WidgetLayout, Season } from "./services/config";
  import type { FilterFormValue } from "./services/formModels";
  import Form from "../forms/Form.svelte";
  import Button from "../forms/Button.svelte";
  import type { FormControl } from "../forms/controls";
  import FilterDropdown from "./controls/FilterDropdown.svelte";
  import ToggleButtons from "./controls/ToggleButtons.svelte";
  import ToggleButton from "./controls/ToggleButton.svelte";
  import CheckboxGroup from "./controls/CheckboxGroup.svelte";
  import GroupOption from "./controls/GroupOption.svelte";
  import type { ControlValidator } from "../forms/validators";
  import type { Control } from "../forms/controls";
  import Input from "../forms/Input.svelte";
  import InputSearch from "./controls/InputSearch.svelte";
  import { derived } from "svelte/store";
  import { debounceStore } from "../forms/stores";
  import InputCheckbox from "../forms/InputCheckbox.svelte";
  import FormActions from "./controls/FormActions.svelte";
  import type { Localizer } from "../l10n/localization.service";
  import type { EnquiryService } from "./services/enquiryService";
  import InputControlErrors from "../forms/InputControlErrors.svelte";
  import FormRequired from "./controls/FormRequired.svelte";

  export let form: FormControl<FilterFormValue>;
  export let layout: WidgetLayout;
  export let T: Localizer;
  export let range: [string, string];
  export let enquiryService: EnquiryService;

  const filterOptions = enquiryService.options.filter || {};

  let dispatch = createEventDispatcher<{ submit: FormControl<FilterFormValue>; back: void }>();

  enquiryService.options.trackStep("filter");

  function handleSubmit(e) {
    if (form.validate(true)) {
      dispatch("submit", form);
    }
  }

  function getSelectedThemesAmenities(value?: { themeIds: string[]; amenetyIds: string[] }) {
    const selected = value ? [...filterOptions.themes.filter((_) => value.themeIds?.includes(_.id)).map((_) => _.name), ...filterOptions.ameneties.filter((_) => value.amenetyIds?.includes(_.id)).map((_) => _.name)] : [];
    return selected;
  }

  function getSelectedAccotypes(value?: { accotypeIds: string[]; boardingIds: string[] }) {
    const selected = value ? [...filterOptions.accotypes.filter((_) => value.accotypeIds?.includes(_.id)).map((_) => _.name), ...filterOptions.boards.filter((_) => value.boardingIds?.includes(_.id)).map((_) => _.name)] : [];
    return selected;
  }

  function getSelectedAreas(value?: { regionIds: string[]; resortIds: string[] }) {
    const selected = value ? [...filterOptions.regions.filter((_) => value.regionIds?.includes(_.id)).map((_) => _.name), ...filterOptions.resorts.filter((_) => value.resortIds?.includes(_.id)).map((_) => _.name)] : [];
    return selected;
  }

  const accotypesValidator: ControlValidator = function accotypesValidator(control: Control<{ accotypeIds: string[] }>) {
    const { accotypeIds } = { ...control.value };

    if (!accotypeIds || accotypeIds.length === 0) {
      return {
        validator: "accotypesValidator",
        message: T.get(`filter.accotypesValidatorText`, control.displayName),
      };
    }
  };

  const areaValidator: ControlValidator = function areaValidator(control: Control<{ regionIds: string[]; resortIds: string[] }>) {
    const { regionIds, resortIds } = { ...control.value };

    if ((!regionIds || regionIds.length === 0) && (!resortIds || resortIds.length === 0)) {
      return {
        validator: "areaValidator",
        message: T.get(`filter.areaValidatorText`, control.displayName),
      };
    }
  };

  const maxCountValidator: ControlValidator = function maxCountValidator(control: FormControl<{ amenetyIds: string[]; themeIds: string[] }>) {
    const maxCount = 80;
    if (accosCount > maxCount && control.validating) {
      return {
        validator: "maxCountValidator",
        message: T.get(`filter.maxCountValidatorText`, control.displayName, maxCount),
      };
    }
  };

  let enquiryCount: EnquiryCount;
  let accosCountLoading = false;

  function handleFormChange() {
    updateCount();
  }

  // init count
  updateCount();

  async function updateCount() {
    try {
      accosCountLoading = true;
      enquiryCount = await enquiryService.getCount(form.value);
      // re-trigger validation if user already triggered it
      if (form.validating) {
        form.validate();
      }
      accosCountLoading = false;
    } catch (e) {
      console.error(e);
    }
  }

  function getCount(enquiryCount: EnquiryCount, key: keyof EnquiryCount, id: number | string, defaultValue = NaN) {
    if (key === "boardingCounts" && typeof id === "string") {
      return id
        .split(",")
        .map((id) => enquiryCount?.[key]?.[+id] ?? defaultValue)
        .reduce((c, n) => c + n, 0);
    }
    return enquiryCount?.[key]?.[id] ?? defaultValue;
  }

  function getCountText(enquiryCount: EnquiryCount, key: keyof EnquiryCount, id: number | string, defaultValue = NaN) {
    const count = getCount(enquiryCount, key, id, defaultValue);
    return !isNaN(count) ? ` (${count})` : "";
  }

  $: accosCount = enquiryCount?.count || 0;

  $: ameneties = filterOptions.ameneties.filter((_) => filterSeason(_.name, _.seasons, range));
  $: themes = filterOptions.themes.filter((_) => filterSeason(_.name, _.seasons, range));

  function filterSeason(name: string, seasons: Season[], [from, to]: [string, string]) {
    const rangeFrom = Date.parse(from);
    const rangeTo = Date.parse(to);


    if (seasons && seasons.length > 0) {
      return seasons.some(overlapWithRange);
    } else {
      return true;
    }

    function overlapWithRange({ begin, end }: Season) {
      const seasonBegin = Date.parse(begin);
      const seasonEnd = Date.parse(end);

      // returns true if range overlps with the season
      // returns false if range falls fully out of the season
      // season:     |----------------|
      // case1:    |----|                     -> true
      // case2:                    |----|     -> true
      // case3:           |----|              -> true
      // case4:    |--------------------|     -> true
      // case5: |---|                         -> false
      // case6:                        |---|  -> false

      var result = (rangeFrom <= seasonBegin && rangeTo >= seasonBegin) || (rangeFrom <= seasonEnd && rangeTo >= seasonEnd) || (rangeFrom >= seasonBegin && rangeFrom <= seasonEnd);


      return result;
    }
  }

  let areasSearch = debounceStore("", 300);
  let regions = derived(
    areasSearch,
    (value, set) => {
      const areas = filterOptions.regions.filter((_) => _.name && _.name.toLowerCase().includes(value.toLowerCase()));
      set(areas);
    },
    filterOptions.regions
  );

  let resorts = derived(
    areasSearch,
    (value, set) => {
      const areas = filterOptions.resorts.filter((_) => _.name && _.name.toLowerCase().includes(value.toLowerCase()));
      set(areas);
    },
    filterOptions.resorts
  );

  function selectAllRegions(checked: boolean, control: Control<{ regionIds: (string | number)[]; resortIds: (string | number)[] }>) {
    control.value = {
      ...control.value,
      regionIds: checked ? filterOptions.regions.map((_) => _.id) : [],
    };
    for (const region of filterOptions.regions) {
      regionChanged({ value: region.id, selected: checked }, control);
    }
  }

  /** toggle all resorts of a region with the region */
  function regionChanged({ value: regionId, selected }, control: Control<{ regionIds: (string | number)[]; resortIds: (string | number)[] }>) {
    let { regionIds = [], resortIds = [] } = control.value;

    const children = filterOptions.resorts.filter((_) => _.parentIds && _.parentIds.includes(regionId));
    if (selected) {
      resortIds = distinct([...resortIds, ...children.map((_) => _.id)]);
    } else {
      const toRemove = children.filter((_) => _.parentIds.filter((_) => regionIds.includes(_)).length == 0).map((_) => _.id as any);
      resortIds = resortIds.filter((id) => !toRemove.includes(id));
    }

    // don't select zero results:
    // resortIds = resortIds.filter(id => getCount(enquiryCount, "resortCounts", id) > 0);

    control.value = {
      regionIds,
      resortIds,
    };

    function distinct(array: any[]) {
      return array.filter((x, i) => array.indexOf(x) == i);
    }
  }

  /** uncheck region if one of its resorts gets unchecked and check it again if all resorts are checked */
  function resortChanged({ value: resortId, selected }, control: Control<{ regionIds: (string | number)[]; resortIds: (string | number)[] }>) {
    const resort = filterOptions.resorts.find((_) => _.id == resortId);
    if (resort?.parentIds) {
      let { regionIds = [], resortIds = [] } = control.value;

      if (selected) {
        for (const parentId of resort.parentIds) {
          const countNotSelected = filterOptions.resorts.filter((_) => _.parentIds.includes(parentId)).filter((_) => !resortIds.includes(_.id)).length;
          if (countNotSelected === 0) {
            regionIds = [...regionIds, parentId];
          }
        }
      } else {
        regionIds = regionIds.filter((id) => !resort.parentIds.includes(id));
      }

      control.value = {
        regionIds,
        resortIds,
      };
    }
  }
</script>

<Form bind:form on:submit={handleSubmit} on:change={handleFormChange} validators={[maxCountValidator]}>
  <div class="mb">
    <FilterDropdown name="accotypes" label="{T.get('filter.accotypesAndBoards')} *" displayName={T.get("filter.accotypes")} overlay="modal" selectedFn={getSelectedAccotypes} validators={[accotypesValidator]} let:control>
      <svelte:fragment slot="title">{T.get("filter.accotypesAndBoards")}</svelte:fragment>

      <div class="mb px">
        <CheckboxGroup name="accotypeIds" title="{T.get('filter.accotypes')} *" let:value required>
          {#each filterOptions.accotypes as accotype (accotype)}
            <GroupOption value={accotype.id} label={accotype.name + getCountText(enquiryCount, "accotypeCounts", accotype.id)} disabled={getCount(enquiryCount, "accotypeCounts", accotype.id) === 0 && !value.includes(accotype.id)} />
          {/each}
        </CheckboxGroup>
      </div>

      <div class="mb px">
        <CheckboxGroup name="boardingIds" title={T.get("filter.boards")} let:value>
          {#each filterOptions.boards as board (board)}
            <GroupOption value={board.id} label={board.name + getCountText(enquiryCount, "boardingCounts", board.id, 0)} disabled={getCount(enquiryCount, "boardingCounts", board.id, 0) === 0 && !value.includes(board.id)} />
          {/each}
        </CheckboxGroup>
      </div>

      <svelte:fragment slot="footer">
        <!-- <InputControlErrors {control} /> -->
        <div class="overlay-action">
          <span>{@html T.get("filter.overlay.accosText", accosCount)}</span>
          <Button on:click={(_) => control.submit()}>{T.get("filter.overlay.done")}</Button>
        </div>
      </svelte:fragment>
    </FilterDropdown>
  </div>

  <div class="mb">
    <FilterDropdown name="areas" label="{T.get('filter.areas')} *" overlay="modal" selectedFn={getSelectedAreas} let:control validators={[areaValidator]} on:close={() => ($areasSearch = "")}>
      <svelte:fragment slot="title">{T.get("filter.areas")} *</svelte:fragment>

      <div class="mb px">
        <InputSearch name="search" placeholder={T.get("filter.searchAreas")} standalone on:change={(e) => ($areasSearch = e.detail.value)} autocomplete="off" />
      </div>

      <div class="mb px">
        <InputCheckbox standalone on:change={(e) => selectAllRegions(e.detail.value, control)}>{T.get("filter.selectAllRegions")}</InputCheckbox>
      </div>

      {#if $regions.length}
        <div class="mb px">
          <CheckboxGroup name="regionIds" title={T.get("filter.regions")} let:value>
            {#each $regions as area (area)}
              <GroupOption value={area.id} label={area.name + getCountText(enquiryCount, "regionCounts", area.id)} disabled={getCount(enquiryCount, "regionCounts", area.id) === 0 && !value.includes(area.id)} on:change={(e) => regionChanged(e.detail, control)} />
            {/each}
          </CheckboxGroup>
        </div>
      {/if}

      {#if $resorts.length}
        <div class="mb px">
          <CheckboxGroup name="resortIds" title={T.get("filter.resorts")} let:value>
            {#each $resorts as area (area)}
              {#if !(getCount(enquiryCount, "resortCounts", area.id) === 0 && !value.includes(area.id))}
                <GroupOption value={area.id} label={area.name + getCountText(enquiryCount, "resortCounts", area.id)} on:change={(e) => resortChanged(e.detail, control)} />
              {/if}
            {/each}
          </CheckboxGroup>
        </div>
      {/if}

      <svelte:fragment slot="footer">
        <!-- <InputControlErrors {control} /> -->
        <div class="overlay-action">
          <span>{@html T.get("filter.overlay.accosText", accosCount)}</span>
          <Button on:click={(_) => control.submit()}>{T.get("filter.overlay.done")}</Button>
        </div>
      </svelte:fragment>
    </FilterDropdown>
  </div>

  <div class="mb">
    <FilterDropdown name="themesAmenities" label={T.get("filter.themesAmeneties")} overlay="modal" selectedFn={getSelectedThemesAmenities} let:control>
      <svelte:fragment slot="title">{T.get("filter.themesAmeneties")}</svelte:fragment>

      <div class="mb">
        <ToggleButtons name="amenetyIds" title={T.get("filter.ameneties")} let:value>
          {#each ameneties as amenety (amenety)}
            <ToggleButton value={amenety.id} label={amenety.name + getCountText(enquiryCount, "amenityCounts", amenety.id, 0)} icon={amenety.icon} disabled={getCount(enquiryCount, "amenityCounts", amenety.id, 0) === 0 && !value.includes(amenety.id)} />
          {/each}
        </ToggleButtons>
      </div>

      <div class="mb">
        <ToggleButtons name="themeIds" title={T.get("filter.themes")} let:value>
          {#each themes as theme (theme)}
            <ToggleButton value={theme.id} label={theme.name + getCountText(enquiryCount, "themesCounts", theme.id)} icon={theme.icon} disabled={getCount(enquiryCount, "themesCounts", theme.id) === 0 && !value.includes(theme.id)} />
          {/each}
        </ToggleButtons>
      </div>

      <svelte:fragment slot="footer">
        <!-- <InputControlErrors {control} /> -->
        <div class="overlay-action">
          <span>{@html T.get("filter.overlay.accosText", accosCount)}</span>
          <Button on:click={(_) => control.submit()}>{T.get("filter.overlay.done")}</Button>
        </div>
      </svelte:fragment>
    </FilterDropdown>
  </div>

  <Input type="hidden" name="wishedOffers" value={enquiryService.options.wishedOffers || 40} />
  <Input type="hidden" name="accosCount" value={accosCount} />

  <div class="form-error">
    <InputControlErrors control={form} mode="alert" />
  </div>

  <FormRequired {T} />

  <FormActions sticky={layout == "default"}>
    <Button secondary on:click={(_) => dispatch("back")}>{T.get("filter.back")}</Button>
    <Button type="submit" disabled={!enquiryCount || enquiryCount.count === 0 || accosCountLoading}>
      {T.get("filter.submitContinueWith")} <i class="load-spinner spin" class:spin={accosCountLoading} />{accosCountLoading ? "" : T.get("filter.submitAccosText", accosCount)}
    </Button>
  </FormActions>
</Form>

<style lang="scss">
  .mb {
    margin-bottom: 1.2rem;
  }

  .px {
    padding-left: 1.2rem;
    padding-right: 1.2rem;
  }

  .overlay-action {
    display: flex;
    align-items: center;
    justify-content: space-between;

    > span {
      font-size: 1.1rem;
    }
  }

  .form-error :global(.input-formfield-errors.alert) {
    margin-top: 1em;
  }
</style>
